import { getSlimcart, pruneDigitalDataObj } from '@insight/toolkit-utils';
import { addToShoppingCartGAE } from '@insight/toolkit-utils/lib/analytics';

/**
 * Builds a map of materialID -> quantity for items being added to cart.
 * Includes warranty material IDs if present.
 *
 * @param {Array<object>} payload - The cart payload items.
 * @returns {object} A map of materialID to quantity.
 */
export function buildItemQuantityMap(payload) {
  return payload.reduce((acc, part) => {
    const { materialID, warrantyDetail, quantity } = part
    const warrantyMaterialID = warrantyDetail?.warrMaterialId
    let partsToAdd = { [materialID]: quantity }
    if (warrantyMaterialID) {
      partsToAdd = { [materialID]: quantity, [warrantyMaterialID]: quantity }
    }
    return { ...acc, ...partsToAdd }
  }, {})
}

/**
 * Builds the modal analytics metadata for accessory carousel tracking.
 *
 * @param {boolean} isAccessoryModal - Whether the add-to-cart originated from an accessory modal.
 * @param {boolean} isEnhancedAccessories - Whether the enhanced accessories feature flag is enabled.
 * @returns {object} Modal analytics data with modalType and optional carousel metrics.
 */
export const getAccessoryModalAnalytics = (isAccessoryModal, isEnhancedAccessories) => {
  const modalData = { modalType: isEnhancedAccessories ? 'Control B' : 'Control A' }
  const carouselDirectionFromStorage = window.sessionStorage.getItem("carouselDirection");
  const carouselCountFromStorage = window.sessionStorage.getItem("carouselCount");
  if (isEnhancedAccessories && isAccessoryModal) {
    modalData.carouselMovement = carouselDirectionFromStorage
    modalData.showing = carouselCountFromStorage
  }
  return modalData
}

/**
 * Fires the add-to-cart analytics event to the dataLayer (non-shopping-request flow).
 * Handles carrier update vs. add tracking, service activation cleanup,
 * and tag manager (ATM/GTM) payload revisions.
 *
 * @param {object} cart - The previous cart state (before add).
 * @param {Array<object>} items - The newly added cart items.
 * @param {boolean} isAccessoryModal - Whether the add originated from an accessory modal.
 * @param {boolean} isEnhancedAccessories - Whether the enhanced accessories feature flag is enabled.
 */
export function trackAddToCartEvent(cart, items, isAccessoryModal, isEnhancedAccessories) {
  if (items.length === 0) return;
  pruneDigitalDataObj();
  window.dataLayer = window.dataLayer || []
  const trackEvent = !!items[0].previousCarrier && items[0].carrier !== items[0].previousCarrier ? 'update' : 'add'

  function addToCartRevise(tmname, obj) {
    if (tmname == "atm") {
      delete obj.ecommerce[trackEvent].products[0].id;
    } else if (tmname == "gtm") {
      obj.ecommerce[trackEvent].products[0].id = obj.ecommerce[trackEvent].products[0].productSku;
      delete obj.ecommerce[trackEvent].products[0].productSku;
      delete obj.ecommerce[trackEvent].products[0].insightPartId;
      delete obj.cart;
    }
    return obj;
  }

  const slimCart = getSlimcart(cart);
  if (trackEvent === 'update') {
    delete items[0].previousCarrier
  }
  if (items[0].serviceType === 'service_activation') {
    delete items[0].contractID
    delete items[0].materialID
  }

  const addToCartPayload = {
    event: 'addToCart',
    ecommerce: {
      currencyCode: items[0].currency,
      [trackEvent]: {
        products: [...items],
      },
    },
    cart: slimCart,
    modal: getAccessoryModalAnalytics(isAccessoryModal, isEnhancedAccessories)
  }
  window.fireTagEvent('addToCart', addToCartPayload, addToCartRevise);
}

/**
 * Fires the shopping-cart analytics event (shopping-request flow).
 * Extracts newly added items from the cart response and sends them to the dataLayer.
 *
 * @param {object} cart - The shopping request cart response.
 * @param {object} itemsToQuantity - Map of materialID to quantity (from buildItemQuantityMap).
 * @param {string} categoryType - The product category type (e.g. 'Accessories', 'Protection Plans').
 * @param {boolean} isAccessoryModal - Whether the add originated from an accessory modal.
 */
export function trackShoppingCartUpdate(cart, itemsToQuantity, categoryType, isAccessoryModal) {
  const { shoppingRequest } = cart
  const {
    cart: { cartItems }
  } = shoppingRequest
  const itemsAddedKeys = Object.keys(itemsToQuantity)
  let newlyAddedItemsData = [];

  // Retrieve items newly added by matching materialId against itemsToQuantity keys
  itemsAddedKeys.forEach(id => {
    cartItems.forEach(item => {
      if (id === item.materialInfo.materialId) {
        newlyAddedItemsData.push(item);
      }
    });
  });

  const itemsAddedToCart = newlyAddedItemsData?.reduce((acc, part) => {
    const itemToAdd = {
      name: part.materialInfo.description,
      id: part.materialInfo.materialId,
      productSku: part.materialInfo.manufacturerPartNumber,
      insightPartId: part.materialInfo.materialId,
      price: part.materialInfo.unitPrice,
      brand: part.materialInfo.manufacturerName,
      category: part.materialInfo.categoryId,
      quantity: itemsToQuantity?.[part.materialInfo.materialId] || part.quantity,
      currency: shoppingRequest.soldTo.currency
    }
    if (categoryType) {
      itemToAdd.categoryType = categoryType
    }
    return acc.concat([itemToAdd])
  }, [])

  addToShoppingCartGAE(
    shoppingRequest.cart,
    [...itemsAddedToCart],
    isAccessoryModal
  )
}

